/**
 * Last War server time. Fixed offset of UTC-2 year-round — the game
 * server does not observe daylight saving. Derived from the user's
 * report that 12:00 ST corresponds to 16:00 CEST (UTC+2).
 */
export const SERVER_UTC_OFFSET_MINUTES = -120;

/** Convert a real Date to the wall-clock time on the server. */
export function toServerDate(d: Date): Date {
  return new Date(d.getTime() + SERVER_UTC_OFFSET_MINUTES * 60_000);
}

/**
 * Read hours/minutes/seconds of the server-side wall clock for a given
 * real Date.
 */
export function serverClock(d: Date): { h: number; m: number; s: number } {
  const s = toServerDate(d);
  return {
    h: s.getUTCHours(),
    m: s.getUTCMinutes(),
    s: s.getUTCSeconds(),
  };
}

/** Format hh:mm:ss for the server clock. */
export function formatServerClock(d: Date): string {
  const { h, m, s } = serverClock(d);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Format hh:mm on the server clock for an ISO timestamp. */
export function formatServerHhmm(iso: string): string {
  const { h, m } = serverClock(new Date(iso));
  return `${pad(h)}:${pad(m)}`;
}

/**
 * Turn a server-local YYYY-MM-DD + HH:MM into a real UTC ISO string.
 * Used to store user-entered server-time values.
 */
export function serverToUtcIso(dateStr: string, hhmm: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [hh, mm] = hhmm.split(':').map(Number);
  const utcMs =
    Date.UTC(y, (mo ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0) -
    SERVER_UTC_OFFSET_MINUTES * 60_000;
  return new Date(utcMs).toISOString();
}

/**
 * Turn a viewer-local YYYY-MM-DD + HH:MM into a real UTC ISO string.
 * The date is treated as the local calendar day; the browser's current
 * timezone determines the offset.
 */
export function localToUtcIso(dateStr: string, hhmm: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [hh, mm] = hhmm.split(':').map(Number);
  return new Date(y!, (mo ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0).toISOString();
}

/** Format HH:MM in the viewer's local timezone for an ISO timestamp. */
export function formatLocalHhmm(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Compute the local hour (0..23) in `timezone` for a given real Date.
 * Uses Intl.DateTimeFormat so DST is handled correctly for player zones.
 */
export function localHourIn(d: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const n = Number(hh);
  return n === 24 ? 0 : n;
}

/**
 * Given a server wall-clock hour and the real "now" Date, return the
 * real Date that represents "when server clock shows that hour" today
 * (rounded to the top of the hour). Used by the ±1h stepper.
 */
export function serverHourToRealDate(serverHour: number, now: Date): Date {
  const s = toServerDate(now);
  // Build a UTC date matching (server date, serverHour:00:00) then shift
  // back by SERVER_UTC_OFFSET_MINUTES to real UTC.
  const base = Date.UTC(
    s.getUTCFullYear(),
    s.getUTCMonth(),
    s.getUTCDate(),
    serverHour,
    0,
    0,
  );
  return new Date(base - SERVER_UTC_OFFSET_MINUTES * 60_000);
}

/**
 * Quality classification for a local hour, from a player's perspective:
 *   - 'bad'  → 00:00–05:59 (deep night / sleep)
 *   - 'meh'  → 06:00–07:59 and 22:00–23:59 (edges)
 *   - 'good' → 08:00–21:59 (waking hours)
 */
export type TimeQuality = 'good' | 'meh' | 'bad';

export function hourQuality(hour: number): TimeQuality {
  const h = ((hour % 24) + 24) % 24;
  if (h < 6) return 'bad';
  if (h < 8 || h >= 22) return 'meh';
  return 'good';
}

export const QUALITY_LABEL: Record<TimeQuality, string> = {
  good: 'Prime',
  meh: 'Edge',
  bad: 'Night',
};

/**
 * Tailwind class bundles per quality — text, background, and bar fills.
 * Kept here so all views share one palette.
 */
export const QUALITY_TEXT: Record<TimeQuality, string> = {
  good: 'text-emerald-300',
  meh: 'text-amber-300',
  bad: 'text-rose-400',
};

export const QUALITY_BAR: Record<TimeQuality, string> = {
  good: 'bg-emerald-500/70 group-hover:bg-emerald-400',
  meh: 'bg-amber-500/70 group-hover:bg-amber-400',
  bad: 'bg-rose-500/70 group-hover:bg-rose-400',
};

/** Common IANA timezones, ordered by rough UTC offset west→east. */
export const COMMON_TIMEZONES: readonly string[] = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Halifax',
  'America/Sao_Paulo',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Auckland',
];
